import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { currentLayerAdapter, originalLayerAdapter } from './layerAdapters'
import type { LocalLayer } from './types'
import {
  editorInitialized,
  editorCleared,
  editorMerged,
  slideSyncFulfilled,
  slideReset,
  allChangesReset,
} from '../editorSync/actions'

const initialState = {
  current: currentLayerAdapter.getInitialState(),
  original: originalLayerAdapter.getInitialState(),
}

export const layerSlice = createSlice({
  name: 'layers',
  initialState,
  reducers: {
    addCurrent(state, action: PayloadAction<LocalLayer>) {
      currentLayerAdapter.addOne(state.current, action.payload)
    },
    updateCurrent(
      state,
      action: PayloadAction<{ id: number; changes: Partial<LocalLayer> }>,
    ) {
      currentLayerAdapter.updateOne(state.current, action.payload)
    },
    removeCurrent(state, action: PayloadAction<number>) {
      currentLayerAdapter.removeOne(state.current, action.payload)
    },
    updateLayerImage(
      state,
      action: PayloadAction<{ id: number; url: string; image_status: string }>,
    ) {
      const { id, url, image_status } = action.payload
      const changes = { url, image_status } as Partial<LocalLayer>

      currentLayerAdapter.updateOne(state.current, { id, changes })
      originalLayerAdapter.updateOne(state.original, { id, changes })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(editorInitialized, (state, action) => {
        currentLayerAdapter.setAll(state.current, action.payload.layers)
        originalLayerAdapter.setAll(state.original, action.payload.layers)
      })
      .addCase(editorCleared, () => initialState)
      .addCase(editorMerged, (state, action) => {
        const { addedLayers, updatedCurrentLayers, updatedOriginalLayers, removedLayerIds } = action.payload

        // Add new layers to both adapters
        if (addedLayers.length > 0) {
          currentLayerAdapter.addMany(state.current, addedLayers)
          originalLayerAdapter.addMany(state.original, addedLayers)
        }

        // Update current layers (partial updates for layers with local changes)
        for (const update of updatedCurrentLayers) {
          currentLayerAdapter.updateOne(state.current, update)
        }

        // Update original layers (full replacements)
        for (const layer of updatedOriginalLayers) {
          originalLayerAdapter.upsertOne(state.original, layer)
        }

        // Remove layers deleted on server
        if (removedLayerIds.length > 0) {
          currentLayerAdapter.removeMany(state.current, removedLayerIds)
          originalLayerAdapter.removeMany(state.original, removedLayerIds)
        }
      })
      .addCase(slideSyncFulfilled, (state, action) => {
        const { slideId, layers } = action.payload

        // Remove old layers for this slide
        const currentAll = currentLayerAdapter.getSelectors().selectAll(state.current)
        const oldIds = currentAll.filter((l) => l.slideId === slideId).map((l) => l.id)

        currentLayerAdapter.removeMany(state.current, oldIds)
        originalLayerAdapter.removeMany(state.original, oldIds)

        // Add new server-authoritative layers
        currentLayerAdapter.addMany(state.current, layers)
        originalLayerAdapter.addMany(state.original, structuredClone(layers))
      })
      .addCase(slideReset, (state, action) => {
        const { slideId } = action.payload

        // Get original layers for this slide
        const origAll = originalLayerAdapter.getSelectors().selectAll(state.original)
        const origLayers = origAll.filter((l) => l.slideId === slideId)

        // Remove current layers for this slide
        const currentAll = currentLayerAdapter.getSelectors().selectAll(state.current)
        const currentIds = currentAll.filter((l) => l.slideId === slideId).map((l) => l.id)

        currentLayerAdapter.removeMany(state.current, currentIds)

        // Restore from original
        currentLayerAdapter.addMany(state.current, structuredClone(origLayers))
      })
      .addCase(allChangesReset, (state) => {
        // Replace all current layers with original
        const origAll = originalLayerAdapter.getSelectors().selectAll(state.original)

        currentLayerAdapter.setAll(state.current, structuredClone(origAll))
      })
  },
})

export const { addCurrent, updateCurrent, removeCurrent, updateLayerImage } = layerSlice.actions
